import { usePrevious } from "@chakra-ui/react";
import { isZero } from "dinero.js";
import { useCommitmentInformationList } from "gen/endpoints/commitment-information/commitment-information";
import { useTransferorInvestorEntityOptionsList } from "gen/endpoints/investor-transfer/investor-transfer";
import { InvestorTransferUpsertDataRequest } from "gen/model/investorTransferUpsertDataRequest";
import React, { useEffect, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SelectInstance } from "react-select";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { formatMoney, moneyFromString } from "shared/utils/MoneyUtils";

export const InvestorTransferDetailsFormFields: React.FC<{
  fundFamilyId: string;
  form: UseFormReturn<InvestorTransferUpsertDataRequest>;
}> = ({ fundFamilyId, form }) => {
  const { t } = useTranslation(["investorTransfer", "common"]);

  const transferorInvestorId = form.watch("transferor_investor_id");
  const previousTransferorInvestorId = usePrevious(transferorInvestorId);

  const {
    data: transferorInvestorEntityOptions,
    isLoading: transferorInvestorEntityOptionsLoading,
  } = useTransferorInvestorEntityOptionsList(fundFamilyId);

  const { data: commitments, isLoading: commitmentInformationLoading } =
    useCommitmentInformationList(
      fundFamilyId,
      { investor_entity_id: transferorInvestorId },
      { query: { enabled: !!transferorInvestorId } },
    );

  const commitmentInformationIsEnabledAndLoading =
    commitmentInformationLoading && !!transferorInvestorId;

  const isLoading =
    transferorInvestorEntityOptionsLoading ||
    commitmentInformationIsEnabledAndLoading;
  const prevLoading = usePrevious(isLoading);

  const investorRef = useRef<SelectInstance>(null);

  useEffect(() => {
    if (
      previousTransferorInvestorId &&
      previousTransferorInvestorId !== transferorInvestorId
    ) {
      form.setValue("security_ownership_ids", []);
    }
  }, [form, previousTransferorInvestorId, transferorInvestorId, isLoading]);

  useEffect(() => {
    // restore focus to investor field after loading
    if (prevLoading && !isLoading && transferorInvestorId) {
      investorRef.current?.focus();
    }
  }, [isLoading, prevLoading, transferorInvestorId]);

  return (
    <>
      <FormSelect
        ref={investorRef}
        name="transferor_investor_id"
        title={t("investorTransfer:transferor")}
        isDisabled={isLoading}
      >
        {transferorInvestorEntityOptions?.map((investorEntity) => (
          <SelectOption
            key={`select_${investorEntity.id}`}
            value={investorEntity.id!}
          >
            {investorEntity.legal_name}
          </SelectOption>
        ))}
      </FormSelect>
      {transferorInvestorId && (
        <FormSelect
          name="security_ownership_ids"
          title={t("investorTransfer:commitment")}
          isDisabled={isLoading}
          isMulti={true}
        >
          {commitments?.map(
            (commitment) =>
              !isZero(moneyFromString(commitment.commitment_amount)) && (
                <SelectOption
                  key={`select_${commitment.id}`}
                  value={commitment.id}
                >
                  {formatMoney(commitment.commitment_amount)}
                </SelectOption>
              ),
          )}
        </FormSelect>
      )}
      <FormDateInput
        name="recorded_date"
        title={t("investorTransfer:transferDate")}
        isDisabled={isLoading}
      />
    </>
  );
};
